import { isArray } from "lodash-es";
import type { IToggle } from "unleash-proxy-client";

import type { UnknownRecord } from "../type";
import { featureToggles } from "./featureToggles";

interface Feature {
  id: string;
  description: string;
  active?: boolean;
  fromGitlab?: boolean;
}

const initialFeatures = [
  { id: "faro-sample-all", description: "Faro sample all description" },
  { id: "gitlab-mrs", description: "Show GitLab merge request links on stickies" },
] as const satisfies readonly Feature[];

export type FeatureId = (typeof initialFeatures)[number]["id"];

let features: Feature[] = initialFeatures.map((f) => ({ ...f }));

export function isFeatureEnabled(route: { query: UnknownRecord }, featureId: FeatureId): boolean {
  const found = features.find((f) => f.id === featureId);
  if (found?.active !== undefined) return found.active;

  return (
    !isFeatureEnabledInQuery(route, "!" + featureId) &&
    (isFeatureEnabledInQuery(route, featureId) || featureToggles.isEnabled(featureId))
  );
}

export function parseUrlWithoutRouter() {
  const hash = window.location.hash;
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) {
    return {};
  }
  const params = new URLSearchParams(hash.substring(queryStart));
  const extractedParams: Record<string, string[]> = {};
  for (const [key, value] of params) {
    if (extractedParams[key]) {
      extractedParams[key].push(value);
    } else {
      extractedParams[key] = [value];
    }
  }
  return extractedParams;
}

function isFeatureEnabledInQuery(route: { query: UnknownRecord }, feature: string): boolean {
  if (!route?.query?.feature) {
    return false;
  }
  if (isArray(route.query.feature)) {
    return route.query.feature.includes(feature);
  }
  return route.query.feature === feature;
}

function broadcastAvailableFeatures(features: Feature[]) {
  window.postMessage({ type: "SET_AVAILABLE_FEATURES", features }, "*");
}

export function broadcastFeatures() {
  window.addEventListener(
    "extensionAvailable",
    () => {
      broadcastAvailableFeatures(features);

      window.addEventListener("message", (event) => {
        if (event.source !== window) return;

        if (event.data.type === "FEATURES_UPDATED") {
          features = event.data.features;

          void triggerSoftRefresh();
        }
      });
    },
    { once: true },
  );
}

export function setGitlabToggles(toggles: IToggle[]) {
  window.postMessage({ type: "SET_GITLAB_TOGGLED_FEATURES", toggles }, "*");
}

async function triggerSoftRefresh() {
  const { router } = await import("@/router/router");
  const currentRoute = router?.currentRoute?.value;
  if (currentRoute && currentRoute.fullPath !== "/") {
    void router.replace({ ...currentRoute, query: { ...currentRoute.query, _t: Date.now() } });
  }
}
